import { handleKeyDown } from "core/model/accessibility/keyboardActions";
import Tooltip from "ds_legacy/components/Tooltip";
import { CHIP_BACKGROUND, configureColor } from "ds_legacy/materials/colors";
import {
  CHIP_HEIGHT,
  CIRCLE_BORDER_RADIUS,
  ROUND_BORDER_RADIUS,
  SQUARE_BORDER_RADIUS,
  border,
  dp,
  margin,
  padding,
} from "ds_legacy/materials/metrics";
import { Chip as ChipText } from "ds_legacy/materials/typography";
import React, { AriaRole, CSSProperties, ReactNode } from "react";
import styled from "styled-components";

export const IconCircle = styled.div<{
  backgroundColor?: string;
  border?: string;
}>`
  border-radius: ${CIRCLE_BORDER_RADIUS};
  height: ${dp(32)};
  width: ${dp(32)};
  margin: ${margin(0)};
  background-color: ${(props) => props.backgroundColor};
  display: flex;
  border: ${(props) => props.border};
  justify-content: center;
  align-items: center;
`;

export const ChipContainer = styled.div<{
  display?: CSSProperties["display"];
  margin?: CSSProperties["margin"];
}>`
  display: ${(props) => props.display || "block"};
  margin: ${(props) => props.margin ?? margin(1, 2, 1, 0)};
`;

type ColorsType = {
  isBlack?: boolean;
  isDisabled?: boolean;
  hasError?: boolean;
  isPrimary?: boolean;
  isWhite?: boolean;
};

const ChipBox = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    ![
      "backgroundColor",
      "borderColor",
      "borderHoverColor",
      "colorOverride",
      "height",
      "isHovered",
      "isSquared",
      "justifyContent",
      "margin",
      "opacity",
      "padding",
      "width",
    ].includes(prop),
})<{
  backgroundColor: ColorsType;
  borderColor: ColorsType;
  borderHoverColor: ColorsType;
  colorOverride?: string;
  height?: CSSProperties["minHeight"];
  isHovered?: boolean;
  justifyContent?: CSSProperties["justifyContent"];
  margin?: string;
  opacity?: number;
  padding?: string;
  isSquared?: boolean;
  width?: CSSProperties["width"];
}>`
  word-break: break-all;
  box-sizing: border-box;
  border-radius: ${({ isSquared }) =>
    isSquared ? SQUARE_BORDER_RADIUS : ROUND_BORDER_RADIUS};
  background-color: ${(props) =>
    props.colorOverride && props.backgroundColor.isPrimary
      ? props.colorOverride
      : configureColor(
          props.theme as any,
          props.backgroundColor,
          props.opacity ? "transparent" : CHIP_BACKGROUND,
          props.isHovered,
        )};
  padding: ${(props) => props.padding || padding(0)};
  margin: ${(props) => props.margin};
  display: flex;
  align-items: center;
  min-height: ${({ height }) => height || dp(CHIP_HEIGHT)};
  cursor: ${(props) => (props.onClick ? "pointer" : "default")};
  border: ${(props) =>
    border({
      color: props.colorOverride
        ? props.colorOverride
        : configureColor(props.theme as any, props.borderColor),
    })};
  opacity: ${({ opacity }) => opacity || 1};
  justify-content: ${({ justifyContent }) => justifyContent || undefined};
  width: ${({ width }) => width};
  &:hover {
    ${(props) =>
      props.onClick &&
      `border: ${border({
        color: configureColor(props.theme as any, props.borderHoverColor),
      })};`}
  }
  @media print {
    break-inside: avoid;
    & {
      height: ${dp(22)};
    }
  }
`;

export type ChipProps = {
  isBold?: boolean;
  children?: React.ReactNode;
  chipMargin?: string;
  chipPadding?: string;
  chipTextFontSize?: string;
  className?: string;
  colorOverride?: string;
  isDisabled?: boolean;
  hasError?: boolean;
  height?: CSSProperties["height"];
  isHovered?: boolean;
  id?: string;
  justifyContent?: CSSProperties["justifyContent"];
  onChange?:
    | ((value: any, statePath?: string) => void)
    | ((value: any) => void);
  onClick?: () => void;
  opacity?: number;
  isPrimary?: boolean;
  isRequired?: boolean;
  role?: Extract<AriaRole, "button" | "checkbox">;
  isSelected?: boolean;
  isSquared?: boolean;
  hasTextBreak?: boolean;
  tooltipText?: string;
  isWhite?: boolean;
  width?: CSSProperties["width"];
};

export function Chip({
  children,
  chipMargin,
  chipPadding,
  className,
  colorOverride,
  hasError,
  hasTextBreak,
  height,
  id,
  isBold = false,
  isDisabled,
  isHovered,
  isPrimary = false,
  isSelected = false,
  isSquared,
  isWhite = true,
  justifyContent,
  onChange,
  onClick,
  opacity,
  role,
  tooltipText,
  width,
}: ChipProps) {
  return (
    <ChipBox
      aria-checked={role ? isSelected : undefined}
      aria-disabled={role ? isDisabled : undefined}
      backgroundColor={{
        isPrimary: isSelected,
        isWhite:
          !opacity &&
          !isSelected &&
          (isDisabled || hasError || isWhite || isPrimary),
      }}
      borderColor={{
        hasError,
        isBlack: isWhite,
        isDisabled,
        isPrimary: isSelected || isPrimary,
      }}
      borderHoverColor={{
        hasError: hasError && !isSelected,
        isBlack: isSelected,
        isPrimary: isWhite,
      }}
      className={className}
      colorOverride={colorOverride}
      data-testid={id}
      height={height}
      id={id}
      isHovered={isHovered}
      isSquared={isSquared}
      justifyContent={justifyContent}
      margin={chipMargin}
      opacity={opacity}
      padding={chipPadding ?? (isSquared ? padding(0, 0.5) : undefined)}
      role={role}
      tabIndex={role ? 0 : undefined}
      width={width}
      onChange={onChange}
      onClick={!isDisabled ? onClick : undefined}
      onKeyDown={
        !isDisabled ? handleKeyDown({ onConfirm: onClick }) : undefined
      }
    >
      <Tooltip title={tooltipText}>
        <ChipText
          data-testid="chip-text"
          disabled={isDisabled}
          hasTextBreak={hasTextBreak}
          isBold={isBold}
          light={isSelected}
          primary={!isSelected && isPrimary}
          style={{
            color: !isSelected && colorOverride ? colorOverride : undefined,
          }}
        >
          {children}
        </ChipText>
      </Tooltip>
    </ChipBox>
  );
}

export function PrintableChip({ children }: { children: ReactNode }) {
  return <Chip isBold>{children}</Chip>;
}
